import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "components/button";

const PinEntryModal = ({
  isOpen,
  onClose,
  onSuccess,
  title = "Enter PIN",
  subtitle = "Enter PIN to continue",
  isWarehouse = false,
}) => {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);
  const inputRefs = useRef([]);

  const CORRECT_PIN = "0000";

  useEffect(() => {
    // Focus first input when modal opens
    if (isOpen && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [isOpen]);

  const handleDigitChange = (index, value) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, "").slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError(false);

    // Auto-focus next input
    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if all digits are filled
    if (newDigits.every((d) => d !== "")) {
      const enteredPin = newDigits.join("");
      if (enteredPin === CORRECT_PIN) {
        setTimeout(() => {
          handleSuccess();
        }, 100);
      } else {
        setError(true);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);
    const newDigits = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
    setDigits(newDigits);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newDigits.findIndex((d) => d === "");
    const focusIndex = nextEmptyIndex === -1 ? 3 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();

    // Check if complete
    if (newDigits.every((d) => d !== "")) {
      const enteredPin = newDigits.join("");
      if (enteredPin === CORRECT_PIN) {
        setTimeout(() => {
          handleSuccess();
        }, 100);
      } else {
        setError(true);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const enteredPin = digits.join("");
    if (enteredPin === CORRECT_PIN) {
      handleSuccess();
    } else if (enteredPin.length === 4) {
      setError(true);
    }
  };

  const handleSuccess = () => {
    setDigits(["", "", "", ""]);
    setError(false);
    onSuccess();
  };

  const handleClose = () => {
    setDigits(["", "", "", ""]);
    setError(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={handleClose}
        />
        <div
          className={`relative min-w-[550px] rounded-2xl shadow-xl ${
            isWarehouse ? "bg-[#030711]" : "bg-white"
          }`}
        >
          {/* Header */}
          <div className="flex flex-col items-center p-6 pb-0">
            <h2
              className={`text-2xl font-semibold ${
                isWarehouse ? "text-[#e6ecff]" : ""
              }`}
            >
              {title}
            </h2>
            <p
              className={`text-sm mb-4 ${
                isWarehouse ? "text-[#a2a7c0]" : "text-gray-600"
              }`}
            >
              {subtitle}
            </p>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 pt-0">
            <div className={`${isWarehouse ? "mb-10 mt-8" : "mb-6"}`}>
              <div className="flex justify-center gap-3">
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="password"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`w-14 h-14 text-center text-2xl font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 ${
                      isWarehouse
                        ? `${error ? "border-red-500" : "border-[#44495e]"} bg-[#030711] text-white focus:ring-[#44495e]`
                        : `${error ? "border-red-500" : "border-gray-300"} focus:ring-blue-500`
                    }`}
                  />
                ))}
              </div>
              {error && (
                <p className="mt-3 text-sm text-red-600 text-center">
                  Incorrect PIN. Please try again.
                </p>
              )}
            </div>

            <div className="flex justify-between gap-64">
              <Button
                type="button"
                onClick={handleClose}
                variant="tertiary"
                className={`flex-1 rounded-lg ${
                  isWarehouse
                    ? "border-[#44495e] border-2 bg-[#030711] hover:bg-[#282828] text-white"
                    : ""
                }`}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!digits.every((d) => d !== "")}
                className={`flex-1 rounded-lg ${
                  isWarehouse
                    ? "bg-[#44495e] hover:bg-[#5a5f73] text-white"
                    : ""
                }`}
              >
                Continue
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PinEntryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  isWarehouse: PropTypes.bool,
};

export default PinEntryModal;
